import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditBlockModal({ 
  block, 
  services,
  staff,
  testimonials,
  gallery,
  theme,
  onClose, 
  onSave,
  onUpdateServices,
  onUpdateStaff,
  onUpdateTestimonials,
  onUpdateGallery,
  fetchData
}) {
  const [formData, setFormData] = useState({ ...block.content });
  const [editingItem, setEditingItem] = useState(block.editService || block.editStaff || null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ content: formData });
  };

  // Service CRUD
  const handleSaveService = async (serviceData) => {
    try {
      if (serviceData.id) {
        await axios.put(`${API}/website/services/${serviceData.id}`, serviceData);
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/website/services`, serviceData);
        toast.success('Service added');
      }
      fetchData();
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await axios.delete(`${API}/website/services/${serviceId}`);
      toast.success('Service deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  // Staff CRUD
  const handleSaveStaff = async (staffData) => {
    try {
      if (staffData.id) {
        await axios.put(`${API}/website/staff/${staffData.id}`, staffData);
        toast.success('Staff member updated');
      } else {
        await axios.post(`${API}/website/staff`, staffData);
        toast.success('Staff member added');
      }
      fetchData();
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to save staff member');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    try {
      await axios.delete(`${API}/website/staff/${staffId}`);
      toast.success('Staff member deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete staff member');
    }
  };

  // Testimonial CRUD
  const handleSaveTestimonial = async (testimonialData) => {
    try {
      if (testimonialData.id) {
        await axios.put(`${API}/website/testimonials/${testimonialData.id}`, testimonialData);
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API}/website/testimonials`, testimonialData);
        toast.success('Testimonial added');
      }
      fetchData();
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleDeleteTestimonial = async (testimonialId) => {
    try {
      await axios.delete(`${API}/website/testimonials/${testimonialId}`);
      toast.success('Testimonial deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  // Gallery CRUD
  const handleSaveGalleryImage = async (imageData) => {
    try {
      if (imageData.id) {
        await axios.put(`${API}/website/gallery/${imageData.id}`, imageData);
        toast.success('Image updated');
      } else {
        await axios.post(`${API}/website/gallery`, imageData);
        toast.success('Image added');
      }
      fetchData();
      setEditingItem(null);
    } catch (error) {
      toast.error('Failed to save image');
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    try {
      await axios.delete(`${API}/website/gallery/${imageId}`);
      toast.success('Image deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const renderBlockEditor = () => {
    switch (block.block_type) {
      case 'hero':
        return (
          <>
            <div className="form-group">
              <label className="form-label">Heading</label>
              <input
                type="text"
                className="form-input"
                value={formData.heading || ''}
                onChange={(e) => handleChange('heading', e.target.value)}
                data-testid="hero-heading-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Subheading</label>
              <input
                type="text"
                className="form-input"
                value={formData.subheading || ''}
                onChange={(e) => handleChange('subheading', e.target.value)}
                data-testid="hero-subheading-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">CTA Button Text</label>
              <input
                type="text"
                className="form-input"
                value={formData.cta_text || ''}
                onChange={(e) => handleChange('cta_text', e.target.value)}
                data-testid="hero-cta-text-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">CTA Link</label>
              <input
                type="text"
                className="form-input"
                value={formData.cta_link || ''}
                onChange={(e) => handleChange('cta_link', e.target.value)}
                data-testid="hero-cta-link-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Background Image URL</label>
              <input
                type="url"
                className="form-input"
                value={formData.background_image || ''}
                onChange={(e) => handleChange('background_image', e.target.value)}
                placeholder="https://..."
                data-testid="hero-bg-input"
              />
              {formData.background_image && (
                <img 
                  src={formData.background_image} 
                  alt="Preview" 
                  style={{ marginTop: '12px', maxWidth: '100%', maxHeight: '150px', borderRadius: '8px' }}
                />
              )}
            </div>
          </>
        );

      case 'services':
        if (block.editService) {
          return <ServiceEditor 
            service={block.editService} 
            onSave={handleSaveService} 
            onCancel={() => onClose()}
          />;
        }
        return (
          <>
            <div className="form-group">
              <label className="form-label">Section Title</label>
              <input
                type="text"
                className="form-input"
                value={formData.section_title || ''}
                onChange={(e) => handleChange('section_title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Services</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {services.map(service => (
                  <div key={service.id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '12px',
                    background: 'var(--editor-bg)',
                    borderRadius: '8px'
                  }}>
                    <div>
                      <strong style={{ color: 'var(--editor-text)' }}>{service.service_name}</strong>
                      <span style={{ color: 'var(--editor-text-muted)', marginLeft: '12px' }}>${service.price}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        type="button"
                        className="toolbar-btn"
                        onClick={() => setEditingItem(service)}
                      >
                        Edit
                      </button>
                      <button 
                        type="button"
                        className="block-control-btn"
                        onClick={() => handleDeleteService(service.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                <button 
                  type="button"
                  className="toolbar-btn"
                  onClick={() => setEditingItem({ service_name: '', price: 0, description: '', image_url: '' })}
                  style={{ marginTop: '8px' }}
                >
                  <Plus size={16} /> Add Service
                </button>
              </div>
            </div>
            {editingItem && editingItem.service_name !== undefined && (
              <ServiceEditor 
                service={editingItem} 
                onSave={handleSaveService} 
                onCancel={() => setEditingItem(null)}
              />
            )}
          </>
        );

      case 'staff':
        if (block.editStaff) {
          return <StaffEditor 
            staff={block.editStaff} 
            onSave={handleSaveStaff} 
            onCancel={() => onClose()}
          />;
        }
        return (
          <>
            <div className="form-group">
              <label className="form-label">Section Title</label>
              <input
                type="text"
                className="form-input"
                value={formData.section_title || ''}
                onChange={(e) => handleChange('section_title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Team Members</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {staff.map(member => (
                  <div key={member.id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '12px',
                    background: 'var(--editor-bg)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {member.photo_url && (
                        <img src={member.photo_url} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                      )}
                      <div>
                        <strong style={{ color: 'var(--editor-text)' }}>{member.name}</strong>
                        <span style={{ color: 'var(--editor-text-muted)', marginLeft: '8px' }}>{member.title}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        type="button"
                        className="toolbar-btn"
                        onClick={() => setEditingItem(member)}
                      >
                        Edit
                      </button>
                      <button 
                        type="button"
                        className="block-control-btn"
                        onClick={() => handleDeleteStaff(member.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                <button 
                  type="button"
                  className="toolbar-btn"
                  onClick={() => setEditingItem({ name: '', title: '', photo_url: '', bio: '' })}
                  style={{ marginTop: '8px' }}
                >
                  <Plus size={16} /> Add Staff Member
                </button>
              </div>
            </div>
            {editingItem && editingItem.name !== undefined && (
              <StaffEditor 
                staff={editingItem} 
                onSave={handleSaveStaff} 
                onCancel={() => setEditingItem(null)}
              />
            )}
          </>
        );

      case 'testimonials':
        return (
          <>
            <div className="form-group">
              <label className="form-label">Testimonials</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {testimonials.map(t => (
                  <div key={t.id} style={{ 
                    padding: '12px',
                    background: 'var(--editor-bg)',
                    borderRadius: '8px'
                  }}>
                    <p style={{ color: 'var(--editor-text)', fontStyle: 'italic', margin: '0 0 8px 0' }}>
                      "{t.quote}"
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--editor-text-muted)' }}>- {t.client_name}</span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          type="button"
                          className="toolbar-btn"
                          onClick={() => setEditingItem(t)}
                        >
                          Edit
                        </button>
                        <button 
                          type="button"
                          className="block-control-btn"
                          onClick={() => handleDeleteTestimonial(t.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <button 
                  type="button"
                  className="toolbar-btn"
                  onClick={() => setEditingItem({ quote: '', client_name: '', rating: 5 })}
                  style={{ marginTop: '8px' }}
                >
                  <Plus size={16} /> Add Testimonial
                </button>
              </div>
            </div>
            {editingItem && editingItem.quote !== undefined && (
              <TestimonialEditor 
                testimonial={editingItem} 
                onSave={handleSaveTestimonial} 
                onCancel={() => setEditingItem(null)}
              />
            )}
          </>
        );

      case 'gallery':
        return (
          <>
            <div className="form-group">
              <label className="form-label">Section Title</label>
              <input
                type="text"
                className="form-input"
                value={formData.section_title || ''}
                onChange={(e) => handleChange('section_title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Gallery Images</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {gallery.map(img => (
                  <div key={img.id} style={{ position: 'relative' }}>
                    <img 
                      src={img.image_url} 
                      alt={img.alt_text}
                      style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                    <button 
                      type="button"
                      className="block-control-btn"
                      onClick={() => handleDeleteGalleryImage(img.id)}
                      style={{ position: 'absolute', top: '4px', right: '4px' }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <button 
                type="button"
                className="toolbar-btn"
                onClick={() => setEditingItem({ image_url: '', alt_text: '' })}
                style={{ marginTop: '12px' }}
              >
                <Plus size={16} /> Add Image
              </button>
            </div>
            {editingItem && editingItem.image_url !== undefined && editingItem.quote === undefined && editingItem.name === undefined && editingItem.service_name === undefined && (
              <GalleryEditor 
                image={editingItem} 
                onSave={handleSaveGalleryImage} 
                onCancel={() => setEditingItem(null)}
              />
            )}
          </>
        );

      case 'text':
        return (
          <div className="form-group">
            <label className="form-label">Text Content</label>
            <textarea
              className="form-textarea"
              value={formData.text || ''}
              onChange={(e) => handleChange('text', e.target.value)}
              rows={6}
              data-testid="text-content-input"
            />
          </div>
        );

      case 'image':
        return (
          <>
            <div className="form-group">
              <label className="form-label">Image URL</label>
              <input
                type="url"
                className="form-input"
                value={formData.url || ''}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://..."
                data-testid="image-url-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Alt Text</label>
              <input
                type="text"
                className="form-input"
                value={formData.alt || ''}
                onChange={(e) => handleChange('alt', e.target.value)}
                placeholder="Describe the image..."
                data-testid="image-alt-input"
              />
            </div>
            {formData.url && (
              <img 
                src={formData.url} 
                alt={formData.alt}
                style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', marginTop: '12px' }}
              />
            )}
          </>
        );

      case 'button':
        return (
          <>
            <div className="form-group">
              <label className="form-label">Button Text</label>
              <input
                type="text"
                className="form-input"
                value={formData.text || ''}
                onChange={(e) => handleChange('text', e.target.value)}
                data-testid="button-text-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Link URL</label>
              <input
                type="text"
                className="form-input"
                value={formData.link || ''}
                onChange={(e) => handleChange('link', e.target.value)}
                placeholder="/booking or https://..."
                data-testid="button-link-input"
              />
            </div>
          </>
        );

      case 'contact_form':
        return (
          <>
            <div className="form-group">
              <label className="form-label">Form Title</label>
              <input
                type="text"
                className="form-input"
                value={formData.form_title || 'Contact Us'}
                onChange={(e) => handleChange('form_title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Submit Button Label</label>
              <input
                type="text"
                className="form-input"
                value={formData.submit_label || 'Send'}
                onChange={(e) => handleChange('submit_label', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Success Message</label>
              <input
                type="text"
                className="form-input"
                value={formData.success_message || ''}
                onChange={(e) => handleChange('success_message', e.target.value)}
              />
            </div>
          </>
        );

      default:
        return (
          <div style={{ color: 'var(--editor-text-muted)' }}>
            No specific settings for this block type.
          </div>
        );
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="edit-block-modal">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Edit {block.block_type.replace('_', ' ')}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {renderBlockEditor()}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="toolbar-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="toolbar-btn primary" data-testid="save-block-btn">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Sub-editors
function ServiceEditor({ service, onSave, onCancel }) {
  const [data, setData] = useState({
    id: service.id,
    service_name: service.service_name || '',
    description: service.description || '',
    price: service.price || 0,
    duration_minutes: service.duration_minutes || 60,
    image_url: service.image_url || '',
    bookable: service.bookable !== false
  });

  return (
    <div style={{ 
      marginTop: '20px', 
      padding: '20px', 
      background: 'var(--editor-toolbar)', 
      borderRadius: '8px',
      border: '1px solid var(--editor-border)'
    }}>
      <h4 style={{ color: 'var(--editor-text)', marginBottom: '16px' }}>
        {service.id ? 'Edit Service' : 'Add New Service'}
      </h4>
      <div className="form-group">
        <label className="form-label">Service Name</label>
        <input
          type="text"
          className="form-input"
          value={data.service_name}
          onChange={(e) => setData(prev => ({ ...prev, service_name: e.target.value }))}
          data-testid="service-name-input"
        />
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-textarea"
          value={data.description}
          onChange={(e) => setData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label className="form-label">Price ($)</label>
          <input
            type="number"
            className="form-input"
            value={data.price}
            onChange={(e) => setData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Duration (minutes)</label>
          <input
            type="number"
            className="form-input"
            value={data.duration_minutes}
            onChange={(e) => setData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 60 }))}
          />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Image URL</label>
        <input
          type="url"
          className="form-input"
          value={data.image_url}
          onChange={(e) => setData(prev => ({ ...prev, image_url: e.target.value }))}
          placeholder="https://..."
        />
      </div>
      <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
        <button type="button" className="toolbar-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="toolbar-btn primary" onClick={() => onSave(data)} data-testid="save-service-btn">
          {service.id ? 'Update Service' : 'Add Service'}
        </button>
      </div>
    </div>
  );
}

function StaffEditor({ staff, onSave, onCancel }) {
  const [data, setData] = useState({
    id: staff.id,
    name: staff.name || '',
    title: staff.title || '',
    specialty: staff.specialty || '',
    bio: staff.bio || '',
    photo_url: staff.photo_url || '',
    show_booking_btn: staff.show_booking_btn !== false
  });

  return (
    <div style={{ 
      marginTop: '20px', 
      padding: '20px', 
      background: 'var(--editor-toolbar)', 
      borderRadius: '8px',
      border: '1px solid var(--editor-border)'
    }}>
      <h4 style={{ color: 'var(--editor-text)', marginBottom: '16px' }}>
        {staff.id ? 'Edit Staff Member' : 'Add New Staff Member'}
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label className="form-label">Name</label>
          <input
            type="text"
            className="form-input"
            value={data.name}
            onChange={(e) => setData(prev => ({ ...prev, name: e.target.value }))}
            data-testid="staff-name-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Title</label>
          <input
            type="text"
            className="form-input"
            value={data.title}
            onChange={(e) => setData(prev => ({ ...prev, title: e.target.value }))}
          />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Specialty</label>
        <input
          type="text"
          className="form-input"
          value={data.specialty}
          onChange={(e) => setData(prev => ({ ...prev, specialty: e.target.value }))}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Bio</label>
        <textarea
          className="form-textarea"
          value={data.bio}
          onChange={(e) => setData(prev => ({ ...prev, bio: e.target.value }))}
          rows={3}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Photo URL</label>
        <input
          type="url"
          className="form-input"
          value={data.photo_url}
          onChange={(e) => setData(prev => ({ ...prev, photo_url: e.target.value }))}
          placeholder="https://..."
        />
      </div>
      <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
        <button type="button" className="toolbar-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="toolbar-btn primary" onClick={() => onSave(data)} data-testid="save-staff-btn">
          {staff.id ? 'Update Staff' : 'Add Staff'}
        </button>
      </div>
    </div>
  );
}

function TestimonialEditor({ testimonial, onSave, onCancel }) {
  const [data, setData] = useState({
    id: testimonial.id,
    quote: testimonial.quote || '',
    client_name: testimonial.client_name || '',
    rating: testimonial.rating || 5
  });

  return (
    <div style={{ 
      marginTop: '20px', 
      padding: '20px', 
      background: 'var(--editor-toolbar)', 
      borderRadius: '8px',
      border: '1px solid var(--editor-border)'
    }}>
      <h4 style={{ color: 'var(--editor-text)', marginBottom: '16px' }}>
        {testimonial.id ? 'Edit Testimonial' : 'Add New Testimonial'}
      </h4>
      <div className="form-group">
        <label className="form-label">Quote</label>
        <textarea
          className="form-textarea"
          value={data.quote}
          onChange={(e) => setData(prev => ({ ...prev, quote: e.target.value }))}
          rows={4}
          data-testid="testimonial-quote-input"
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label className="form-label">Client Name</label>
          <input
            type="text"
            className="form-input"
            value={data.client_name}
            onChange={(e) => setData(prev => ({ ...prev, client_name: e.target.value }))}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Rating</label>
          <select
            className="form-input"
            value={data.rating}
            onChange={(e) => setData(prev => ({ ...prev, rating: parseInt(e.target.value) }))}
          >
            {[5, 4, 3, 2, 1].map(r => (
              <option key={r} value={r}>{r} Stars</option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
        <button type="button" className="toolbar-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="toolbar-btn primary" onClick={() => onSave(data)} data-testid="save-testimonial-btn">
          {testimonial.id ? 'Update Testimonial' : 'Add Testimonial'}
        </button>
      </div>
    </div>
  );
}

function GalleryEditor({ image, onSave, onCancel }) {
  const [data, setData] = useState({
    id: image.id,
    image_url: image.image_url || '',
    alt_text: image.alt_text || ''
  });

  return (
    <div style={{ 
      marginTop: '20px', 
      padding: '20px', 
      background: 'var(--editor-toolbar)', 
      borderRadius: '8px',
      border: '1px solid var(--editor-border)'
    }}>
      <h4 style={{ color: 'var(--editor-text)', marginBottom: '16px' }}>
        {image.id ? 'Edit Image' : 'Add New Image'}
      </h4>
      <div className="form-group">
        <label className="form-label">Image URL</label>
        <input
          type="url"
          className="form-input"
          value={data.image_url}
          onChange={(e) => setData(prev => ({ ...prev, image_url: e.target.value }))}
          placeholder="https://..."
          data-testid="gallery-url-input"
        />
      </div>
      <div className="form-group">
        <label className="form-label">Alt Text</label>
        <input
          type="text"
          className="form-input"
          value={data.alt_text}
          onChange={(e) => setData(prev => ({ ...prev, alt_text: e.target.value }))}
          placeholder="Describe the image..."
        />
      </div>
      {data.image_url && (
        <img 
          src={data.image_url} 
          alt={data.alt_text}
          style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', marginTop: '12px' }}
        />
      )}
      <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
        <button type="button" className="toolbar-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="toolbar-btn primary" onClick={() => onSave(data)} data-testid="save-gallery-btn">
          {image.id ? 'Update Image' : 'Add Image'}
        </button>
      </div>
    </div>
  );
}
